import React, { useEffect, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import { useDispatch, useSelector } from "react-redux";
import {
  Table, Tag, Button, Modal, Select, Input,
  Popconfirm, Upload, Form, DatePicker, Spin, Tooltip, Empty, Popover,
} from "antd";
import dayjs from "dayjs";
import api from "../../../core/axios/axiosInstance";
import {
  fetchKYCDocuments,
  uploadKYCDocument,
  updateKYCDocument,
  deleteKYCDocument,
  fetchIdentities,
  createIdentity,
  archiveIdentity,
} from "../../../core/data/redux/slices/kycDocumentSlice";
// Use the same countries source as Browse Numbers (/v2/numbers/countries)
// These have { id, name, short_name } and are already filtered to countries
// where numbers are available — which is what KYC documents are for.
import { fetchInventoryCountries } from "../../../core/data/redux/slices/numberInventorySlice";
import { getDialCode } from "../../../core/utils/dialCodes";
import "./KYCDocuments.css";

/** Build the suffix appended to identity names so the backend can scope them.
 *  Format: "-{firstname}({firstPhone})"  e.g. "-Waqar(+9715555555555)"
 *  If no name/phone available, returns empty string (no suffix). */
export function buildIdentitySuffix(profile) {
  const firstname = profile?.firstname || "";
  const phone     = (profile?.phonenumbers || [])[0] || "";
  if (!firstname && !phone) return "";
  return `-${firstname}${phone ? `(${phone})` : ""}`;
}

/** Strip the auto-appended suffix from an identity name for display.
 *  e.g. "Dubai-Waqar(+9715555555555)" → "Dubai"    */
export function displayIdentityName(fullName, suffix) {
  if (!fullName || !suffix) return fullName || "";
  return fullName.endsWith(suffix) ? fullName.slice(0, -suffix.length) : fullName;
}

const { Option } = Select;
const { RangePicker } = DatePicker;

// ── Constants ─────────────────────────────────────────────────────────────────
const DOC_TYPE_OPTIONS = [
  {
    value: "personal_id",
    label: "Personal ID",
    icon: "ti-id-badge",
    color: "#1677ff",
    bg: "#e8f4ff",
    tagColor: "blue",
    hasBackside: true,
  },
  {
    value: "address_proof",
    label: "Proof of Address",
    icon: "ti-home",
    color: "#fa8c16",
    bg: "#fff7e0",
    tagColor: "orange",
    hasBackside: false,
  },
];

const STATUS_CFG = {
  pending:  { color: "#fa8c16", bg: "#fff7e6", border: "#ffd591", label: "Pending Review", icon: "ti-clock"       },
  approved: { color: "#52c41a", bg: "#f6ffed", border: "#b7eb8f", label: "Approved",       icon: "ti-circle-check"},
  rejected: { color: "#cf1322", bg: "#fff1f0", border: "#ffa39e", label: "Rejected",       icon: "ti-circle-x"   },
  expired:  { color: "#8c8c8c", bg: "#f5f5f5", border: "#d9d9d9", label: "Expired",        icon: "ti-calendar-x" },
  "in review": { color: "#722ed1", bg: "#f9f0ff", border: "#d3adf7", label: "In Review", icon: "ti-eye" },
};

const ACCEPTED_MIME  = ["image/jpeg", "image/png", "application/pdf"];
const ACCEPTED_EXTS  = ".jpg,.jpeg,.png,.pdf";
const MAX_FILE_BYTES = 20 * 1024 * 1024; // 20 MB (per DIDLogic spec)

// ── Helpers ───────────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const s = status?.toLowerCase();
  const cfg = STATUS_CFG[s] || { color: "#8c8c8c", bg: "#f5f5f5", border: "#d9d9d9", label: status || "Unknown", icon: "ti-help" };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: cfg.bg, border: `1px solid ${cfg.border}`,
      borderRadius: 20, padding: "3px 12px",
      color: cfg.color, fontSize: 12, fontWeight: 600,
    }}>
      <i className={`ti ${cfg.icon}`} />{cfg.label}
    </span>
  );
}

function DocTypeTag({ docType }) {
  const opt = DOC_TYPE_OPTIONS.find((o) => o.value === docType);
  return (
    <Tag color={opt?.tagColor || "default"} style={{ borderRadius: 20 }}>
      {opt ? <><i className={`ti ${opt.icon} me-1`} />{opt.label}</> : docType}
    </Tag>
  );
}

function FileDragger({ label, file, onFile, onRemove, accept, error }) {
  const beforeUpload = (f) => {
    if (!ACCEPTED_MIME.includes(f.type)) { onFile(null, "Only JPG, JPEG, PNG, PDF allowed."); return Upload.LIST_IGNORE; }
    if (f.size > MAX_FILE_BYTES)         { onFile(null, "Max file size is 20 MB.");            return Upload.LIST_IGNORE; }
    onFile(f, "");
    return false;
  };

  // When file is selected, show a custom row with always-visible red delete button
  if (file) {
    return (
      <div>
        <div style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "12px 14px", borderRadius: 10,
          border: "1.5px solid #52c41a", background: "#f6ffed",
        }}>
          <i className="ti ti-file-check" style={{ fontSize: 24, color: "#52c41a", flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, color: "#1a1a2e", fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {file.name}
            </div>
            <div style={{ color: "#8c8c8c", fontSize: 11 }}>{(file.size / 1024).toFixed(1)} KB</div>
          </div>
          <button
            type="button"
            onClick={() => onRemove()}
            title="Remove file"
            style={{
              background: "#ff4d4f", border: "none", borderRadius: "50%",
              width: 26, height: 26, flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", color: "#fff", fontSize: 13,
            }}
          >
            <i className="ti ti-x" />
          </button>
        </div>
        {error && <p style={{ color: "#cf1322", fontSize: 12, marginTop: 4 }}><i className="ti ti-alert-circle me-1" />{error}</p>}
      </div>
    );
  }

  return (
    <div>
      <Upload.Dragger
        accept={accept}
        beforeUpload={beforeUpload}
        maxCount={1}
        showUploadList={false}
        style={{ borderRadius: 10 }}
      >
        <div style={{ padding: "14px 0" }}>
          <i className="ti ti-cloud-upload" style={{ fontSize: 30, color: "#667eea", display: "block", marginBottom: 6 }} />
          <p style={{ margin: 0, fontWeight: 600, fontSize: 13, color: "#1a1a2e" }}>{label}</p>
          <p style={{ margin: 0, color: "#8c8c8c", fontSize: 11 }}>jpg, jpeg, png, pdf — max 20 MB</p>
        </div>
      </Upload.Dragger>
      {error && <p style={{ color: "#cf1322", fontSize: 12, marginTop: 4 }}><i className="ti ti-alert-circle me-1" />{error}</p>}
    </div>
  );
}

// ── Create Identity Modal ─────────────────────────────────────────────────────
const CreateIdentityModal = ({ open, onClose }) => {
  const dispatch  = useDispatch();
  const { creatingIdentity } = useSelector((s) => s.kycDocuments);
  const profile   = useSelector((s) => s.profile);
  const [name, setName] = useState("");

  const handleSubmit = async () => {
    if (!name.trim()) return;
    // Append "-{firstname}({phone})" so the backend can scope identities per user.
    // e.g. "Dubai-Waqar(+9715555555555)"
    // The suffix is stripped before display (see displayIdentityName).
    const suffix   = buildIdentitySuffix(profile);
    const fullName = suffix ? `${name.trim()}${suffix}` : name.trim();
    const res = await dispatch(createIdentity(fullName));
    if (!res.error) {
      setName("");
      onClose();
      dispatch(fetchIdentities());
    }
  };

  const handleClose = () => { setName(""); onClose(); };

  return (
    <Modal
      title={null}
      open={open}
      onCancel={handleClose}
      onOk={handleSubmit}
      confirmLoading={creatingIdentity}
      okText="Create Identity"
      cancelText="Cancel"
      okButtonProps={{
        disabled: !name.trim(),
        style: { background: name.trim() ? "linear-gradient(135deg,#3C2371,#8e77fa)" : undefined, border: "none", borderRadius: 8 },
      }}
      cancelButtonProps={{ style: { borderRadius: 8 } }}
      width={440}
      destroyOnClose
    >
      {/* Header */}
      <div style={{
        background: "linear-gradient(135deg,#3C2371 0%,#5B6ABF 55%,#8e77fa 100%)",
        borderRadius: "8px 8px 0 0", padding: "18px 24px",
        margin: "-20px -24px 24px", color: "#fff",
      }}>
        <h5 style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
          <i className="ti ti-user-plus" style={{ marginRight: 8 }} />Create Identity
        </h5>
        <p style={{ color: "rgba(255,255,255,0.7)", margin: "4px 0 0", fontSize: 12 }}>
          An identity groups KYC documents for a specific end-user
        </p>
      </div>

      <div style={{
        background: "#f0f5ff", border: "1px solid #adc6ff",
        borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 12, color: "#4a6fa5",
      }}>
        <i className="ti ti-info-circle" style={{ marginRight: 6 }} />
        After creating, upload documents (Personal ID, Proof of Address) under this identity.
        Select it when purchasing numbers that require KYC.
      </div>

      <Form layout="vertical" requiredMark={false}>
        <Form.Item
          label={<span style={{ fontWeight: 600 }}>Identity Name <span style={{ color: "#cf1322" }}>*</span></span>}
          extra={
            <span style={{ color: "#8c8c8c", fontSize: 11 }}>
              Examples: <em>John Doe</em>  · <em>Dubai</em>
            </span>
          }
        >
          <Input
            size="large"
            placeholder="e.g. John Doe"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onPressEnter={handleSubmit}
            autoFocus
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// ── Identities Section ────────────────────────────────────────────────────────
const IdentitySection = ({ onCreateOpen }) => {
  const dispatch = useDispatch();
  const { identities, identitiesLoading, archivingIdentity } = useSelector((s) => s.kycDocuments);
  const suffix = buildIdentitySuffix(useSelector((s) => s.profile));

  const columns = [
    {
      title: "Identity",
      key: "identity",
      render: (_, r) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10, background: "#f0f4ff", flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <i className="ti ti-user" style={{ fontSize: 20, color: "#667eea" }} />
          </div>
          <div>
            <div style={{ fontWeight: 600, color: "#1a1a2e", fontSize: 14 }}>
              {displayIdentityName(r.name, suffix)}
            </div>
            <div style={{ fontSize: 11, color: "#8c8c8c" }}>ID #{r.id}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Documents",
      dataIndex: "documents_count",
      align: "center",
      render: (v) => (
        <span style={{ fontWeight: 600, color: v > 0 ? "#52c41a" : "#8c8c8c" }}>
          {v ?? 0}
        </span>
      ),
    },
    {
      title: "Status",
      key: "status",
      render: (_, r) => {
        const isActive = !r.archived && r.persistence_status !== "archived";
        return (
          <Tag
            color={isActive ? "green" : "default"}
            style={{ borderRadius: 20 }}
          >
            {isActive ? "Active" : "Archived"}
          </Tag>
        );
      },
    },
    {
      title: "Default",
      key: "default",
      align: "center",
      render: (_, r) => r.is_default
        ? <Tag color="blue" style={{ borderRadius: 20 }}>Default</Tag>
        : <span style={{ color: "#bfbfbf" }}>—</span>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_, r) => {
        if (r.is_default) {
          return (
            <Tooltip title="Default identity cannot be archived">
              <Button size="small" disabled style={{ borderRadius: 8 }}>
                <i className="ti ti-lock" />
              </Button>
            </Tooltip>
          );
        }
        if (r.archived || r.persistence_status === "archived") {
          return <Tag style={{ borderRadius: 20 }}>Archived</Tag>;
        }
        return (
          <Popconfirm
            title="Archive this identity?"
            description="Documents are preserved. The identity can no longer be used for new purchases."
            onConfirm={() => dispatch(archiveIdentity(r.id))}
            okText="Archive" okButtonProps={{ danger: true }}
            cancelText="Cancel"
          >
            <Button
              size="small"
              danger
              loading={archivingIdentity === r.id}
              style={{ borderRadius: 8 }}
            >
              {archivingIdentity !== r.id && <i className="ti ti-archive" />}
              {archivingIdentity !== r.id ? " Archive" : ""}
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  return (
    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #f0f0f0", marginBottom: 24, overflow: "hidden" }}>
      {/* Section header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "14px 20px", borderBottom: "1px solid #f0f0f0",
      }}>
        <div>
          <h5 style={{ fontWeight: 700, margin: 0, fontSize: 15 }}>
            <i className="ti ti-users" style={{ marginRight: 8, color: "#667eea" }} />
            Identities
          </h5>
          <p style={{ color: "#8c8c8c", margin: "2px 0 0", fontSize: 12 }}>
            Group KYC documents by end-user. Select an identity when purchasing numbers.
          </p>
        </div>
        <Button
          type="primary"
          style={{ borderRadius: 8, background: "linear-gradient(135deg,#3C2371,#8e77fa)", border: "none" }}
          onClick={onCreateOpen}
        >
          <i className="ti ti-plus" style={{ marginRight: 6 }} />New Identity
        </Button>
      </div>

      {/* Table */}
      <Table
        dataSource={identities}
        columns={columns}
        rowKey="id"
        loading={identitiesLoading}
        pagination={false}
        scroll={{ x: "max-content" }}
        locale={{
          emptyText: (
            <div style={{ padding: "32px 0", textAlign: "center" }}>
              <i className="ti ti-users" style={{ fontSize: 36, color: "#bfbfbf", display: "block", marginBottom: 10 }} />
              <p style={{ color: "#8c8c8c", marginBottom: 12 }}>No identities yet</p>
              <Button
                type="primary"
                size="small"
                style={{ borderRadius: 8, background: "linear-gradient(135deg,#3C2371,#8e77fa)", border: "none" }}
                onClick={onCreateOpen}
              >
                <i className="ti ti-user-plus" style={{ marginRight: 6 }} />
                Create your first identity
              </Button>
            </div>
          ),
        }}
      />
    </div>
  );
};

// ── Upload Modal ──────────────────────────────────────────────────────────────
const UploadModal = ({ open, onClose, onDone, onCreateIdentity }) => {
  const dispatch = useDispatch();
  const { uploading, identities, identitiesLoading } = useSelector((s) => s.kycDocuments);
  const suffix = buildIdentitySuffix(useSelector((s) => s.profile));
  // Use number inventory countries — same source as Browse Numbers, always loaded
  const { countries, countriesLoading } = useSelector((s) => s.numberInventory);
  const [form]          = Form.useForm();
  const [docType,   setDocType]   = useState(null);
  const [frontFile, setFrontFile] = useState(null);
  const [backFile,  setBackFile]  = useState(null);
  const [frontErr,  setFrontErr]  = useState("");
  const [backErr,   setBackErr]   = useState("");

  const selectedOpt = DOC_TYPE_OPTIONS.find((o) => o.value === docType);

  const handleClose = () => {
    form.resetFields();
    setDocType(null); setFrontFile(null); setBackFile(null);
    setFrontErr(""); setBackErr("");
    onClose();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!frontFile) { setFrontErr("File is required."); return; }

      const fd = new FormData();
      fd.append("doc_type",    values.doc_type);
      fd.append("name",        values.name);
      fd.append("identity_id", values.identity_id);
      fd.append("frontside",   frontFile);
      if (backFile)         fd.append("backside", backFile);
      if (values.country)   fd.append("country",  values.country);

      const result = await dispatch(uploadKYCDocument(fd));
      if (!result.error) { handleClose(); onDone(); }
    } catch (_) {}
  };

  return (
    <Modal
      title={null}
      open={open}
      onCancel={handleClose}
      onOk={handleSubmit}
      confirmLoading={uploading}
      okText="Upload Document"
      cancelText="Cancel"
      okButtonProps={{ style: { background: "linear-gradient(135deg,#3C2371,#8e77fa)", border: "none", borderRadius: 8 } }}
      cancelButtonProps={{ style: { borderRadius: 8 } }}
      width={580}
      destroyOnClose
    >
      {/* Header */}
      <div style={{
        background: "linear-gradient(135deg,#3C2371 0%,#5B6ABF 55%,#8e77fa 100%)",
        borderRadius: "8px 8px 0 0", padding: "20px 24px",
        margin: "-20px -24px 24px", color: "#fff",
      }}>
        <h5 style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
          <i className="ti ti-upload me-2" />Upload KYC Document
        </h5>
        <p style={{ color: "rgba(255,255,255,0.75)", margin: "4px 0 0", fontSize: 13 }}>
          Supported formats: jpg, jpeg, png, pdf — max 20 MB per file
        </p>
      </div>

      <Form form={form} layout="vertical" requiredMark={false}>

        {/* 1. Document Type */}
        <Form.Item
          label={<span style={{ fontWeight: 600 }}>Document Type <span style={{ color: "#cf1322" }}>*</span></span>}
          name="doc_type"
          rules={[{ required: true, message: "Select a document type" }]}
        >
          {/*
            optionLabelProp="label" → shows only the plain text label in the closed/selected state.
            Without it, Ant Design renders the full JSX child (icon+hint) inside the select box.
          */}
          <Select
            size="large"
            placeholder="Select document type..."
            optionLabelProp="label"
            onChange={(v) => { setDocType(v); setBackFile(null); setBackErr(""); }}
          >
            {DOC_TYPE_OPTIONS.map((o) => (
              <Option
                key={o.value}
                value={o.value}
                label={o.label}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "2px 0" }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 6, background: o.bg, flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <i className={`ti ${o.icon}`} style={{ color: o.color, fontSize: 15 }} />
                  </div>
                  <span style={{ fontWeight: 600, fontSize: 13, color: "#1a1a2e" }}>{o.label}</span>
                </div>
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* 2. Country */}
        <Form.Item
          label={<span style={{ fontWeight: 600 }}>Country <span style={{ color: "#cf1322" }}>*</span></span>}
          name="country"
          rules={[{ required: true, message: "Select a country" }]}
          extra="Select the country this document was issued in"
        >
          {/*
            Normalize each country defensively — DIDLogic may return
            { short_name, name } or { country_iso, country_name } or { code, name }.
            We extract a 2-letter ISO code for both the value and the flag.
          */}
          {/*
            Value sent to DIDLogic = country.name (e.g. "India", "United States")
            Flag rendered using country.short_name (ISO-2, e.g. "IN", "US")
            Source: /v2/numbers/countries → { id, name, short_name }
          */}
          <Select
            size="large"
            showSearch
            placeholder={countriesLoading ? "Loading countries…" : "Search country…"}
            loading={countriesLoading}
            optionLabelProp="label"
            filterOption={(input, option) =>
              option?.searchlabel?.toLowerCase().includes(input.toLowerCase())
            }
          >
            {(countries || []).map((c) => (
              <Option
                key={c.id || c.short_name}
                value={c.name}
                label={`${c.name}${getDialCode(c.short_name) ? ` (${getDialCode(c.short_name)})` : ""}`}
                searchlabel={`${c.name} ${c.short_name || ""} ${getDialCode(c.short_name) || ""}`}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "2px 0" }}>
                  {c.short_name && (
                    <ReactCountryFlag
                      countryCode={c.short_name}
                      svg
                      style={{ width: 24, height: 18, borderRadius: 3, objectFit: "cover", flexShrink: 0 }}
                    />
                  )}
                  <span style={{ fontWeight: 500 }}>{c.name}</span>
                  {getDialCode(c.short_name) && (
                    <span style={{ color: "#667eea", fontSize: 11, fontWeight: 600, marginLeft: "auto" }}>
                      {getDialCode(c.short_name)}
                    </span>
                  )}
                </div>
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* 3. Document Name */}
        <Form.Item
          label={<span style={{ fontWeight: 600 }}>Document Name <span style={{ color: "#cf1322" }}>*</span></span>}
          name="name"
          extra={docType === "personal_id" ? "e.g. Driver's license (US), UAE Passport" : "e.g. Electricity bill (Dubai), Bank statement"}
          rules={[{ required: true, message: "Enter a document name" }]}
        >
          <Input
            size="large"
            placeholder={
              docType === "personal_id"
                ? "e.g. Driver's license (US)"
                : docType === "address_proof"
                ? "e.g. Electricity bill (Dubai)"
                : "e.g. Driver's license (US)"
            }
          />
        </Form.Item>

        {/* 4. Identity */}
        <Form.Item
          label={
            <span style={{ fontWeight: 600 }}>
              Identity <span style={{ color: "#cf1322" }}>*</span>
              <Tooltip title="An Identity is a set of documents linked to a specific end-user. When purchasing a number, select the identity whose documents cover that user.">
                <i className="ti ti-info-circle ms-1" style={{ color: "#8c8c8c" }} />
              </Tooltip>
            </span>
          }
          name="identity_id"
          extra="Group documents under the correct end-user identity"
          rules={[{ required: true, message: "Select an identity" }]}
        >
          <Select
            size="large"
            placeholder={identitiesLoading ? "Loading identities..." : "Select identity..."}
            loading={identitiesLoading}
            showSearch
            filterOption={(input, option) =>
              option?.searchlabel?.toLowerCase().includes(input.toLowerCase())
            }
            optionLabelProp="label"
          >
            {identities.map((id) => {
              const displayName = displayIdentityName(id.name, suffix);
              return (
                <Option key={id.id} value={id.id} label={displayName} searchlabel={displayName}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontWeight: 500 }}>{displayName}</span>
                    <Tag color={id.persistence_status === "active" ? "green" : "default"} style={{ marginLeft: 8 }}>
                      {id.persistence_status || "active"}
                    </Tag>
                  </div>
                </Option>
              );
            })}
          </Select>
          {/* Fix 6: prompt to create identity when none exist */}
          {!identitiesLoading && identities.length === 0 && (
            <div style={{
              marginTop: 8, padding: "10px 14px",
              background: "#fff7e6", border: "1px solid #ffd591",
              borderRadius: 8, fontSize: 12, color: "#613400",
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <span><i className="ti ti-alert-triangle" style={{ marginRight: 6 }} />No identities yet.</span>
              <button
                type="button"
                onClick={onCreateIdentity}
                style={{ background: "none", border: "none", color: "#3C2371", fontWeight: 700, cursor: "pointer", fontSize: 12 }}
              >
                + Create Identity
              </button>
            </div>
          )}
        </Form.Item>

        {/* 5. File upload */}
        <Form.Item label={
          <span style={{ fontWeight: 600 }}>
            {selectedOpt?.hasBackside ? "Frontside File" : "File"}
            {" "}<span style={{ color: "#cf1322" }}>*</span>
          </span>
        }>
          <FileDragger
            label={selectedOpt?.hasBackside ? "Click or drag frontside (required)" : "Click or drag file here"}
            file={frontFile}
            onFile={(f, err) => { setFrontFile(f); setFrontErr(err); }}
            onRemove={() => { setFrontFile(null); setFrontErr(""); }}
            accept={ACCEPTED_EXTS}
            error={frontErr}
          />
        </Form.Item>

        {/* Backside — only for Personal ID */}
        {selectedOpt?.hasBackside && (
          <Form.Item label={<span style={{ fontWeight: 600 }}>Backside File <span style={{ color: "#8c8c8c", fontWeight: 400 }}>(optional, for ID cards with back)</span></span>}>
            <FileDragger
              label="Click or drag backside file here"
              file={backFile}
              onFile={(f, err) => { setBackFile(f); setBackErr(err); }}
              onRemove={() => { setBackFile(null); setBackErr(""); }}
              accept={ACCEPTED_EXTS}
              error={backErr}
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

// ── Edit Document Modal (name + identity) ────────────────────────────────────
const EditModal = ({ doc, open, onClose, onDone }) => {
  const dispatch = useDispatch();
  const { updating, identities, identitiesLoading } = useSelector((s) => s.kycDocuments);
  const suffix = buildIdentitySuffix(useSelector((s) => s.profile));
  const [form] = Form.useForm();

  useEffect(() => {
    if (doc && open) {
      form.setFieldsValue({
        name:        doc.name          || "",
        identity_id: doc.identity_id   || undefined,
      });
    }
  }, [doc, open, form]);

  const handleSubmit = async () => {
    try {
      const values  = await form.validateFields();
      const payload = { id: doc.id, name: values.name };
      if (values.identity_id) payload.identity_id = values.identity_id;
      const result = await dispatch(updateKYCDocument(payload));
      if (!result.error) { onClose(); onDone(); }
    } catch (_) {}
  };

  return (
    <Modal
      title={null}
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={updating}
      okText="Save Changes"
      cancelText="Cancel"
      okButtonProps={{ style: { background: "linear-gradient(135deg,#3C2371,#8e77fa)", border: "none", borderRadius: 8 } }}
      cancelButtonProps={{ style: { borderRadius: 8 } }}
      width={460}
      destroyOnClose
    >
      <div style={{
        background: "linear-gradient(135deg,#3C2371 0%,#5B6ABF 55%,#8e77fa 100%)",
        borderRadius: "8px 8px 0 0", padding: "18px 24px",
        margin: "-20px -24px 24px", color: "#fff",
      }}>
        <h5 style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
          <i className="ti ti-edit" style={{ marginRight: 8 }} />Edit Document
        </h5>
      </div>
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          label={<span style={{ fontWeight: 600 }}>Document Name <span style={{ color: "#cf1322" }}>*</span></span>}
          name="name"
          rules={[{ required: true, message: "Name is required" }]}
        >
          <Input size="large" />
        </Form.Item>
        <Form.Item
          label={<span style={{ fontWeight: 600 }}>Identity</span>}
          name="identity_id"
        >
          <Select
            size="large"
            placeholder="Select identity…"
            loading={identitiesLoading}
            showSearch
            allowClear
            filterOption={(input, option) =>
              option?.searchlabel?.toLowerCase().includes(input.toLowerCase())
            }
            optionLabelProp="label"
          >
            {identities.map((id) => {
              const displayName = displayIdentityName(id.name, suffix);
              return (
                <Option key={id.id} value={id.id} label={displayName} searchlabel={displayName}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontWeight: 500 }}>{displayName}</span>
                    <Tag color={id.persistence_status === "active" ? "green" : "default"} style={{ marginLeft: 8 }}>
                      {id.persistence_status || "active"}
                    </Tag>
                  </div>
                </Option>
              );
            })}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const KYCDocuments = () => {
  const dispatch = useDispatch();
  const { documents, loading, deleting, identities } =
    useSelector((s) => s.kycDocuments);
  const suffix = buildIdentitySuffix(useSelector((s) => s.profile));

  // Filters
  const [statusFilter,   setStatusFilter]   = useState(undefined);
  const [typeFilter,     setTypeFilter]     = useState(undefined);
  const [identityFilter, setIdentityFilter] = useState(undefined);
  const [dateRange,      setDateRange]      = useState([null, null]);
  const [page, setPage] = useState(1);

  // Modals
  const [uploadOpen,         setUploadOpen]         = useState(false);
  const [createIdentityOpen, setCreateIdentityOpen] = useState(false);
  const [editDoc,            setEditDoc]            = useState(null);
  const [downloading,        setDownloading]        = useState(null);

  // Build params from current filter state
  const buildParams = (overrides = {}) => {
    const p = { page, per_page: 20 };
    if (statusFilter)   p.approvement_status = statusFilter;
    if (typeFilter)     p.doc_type           = typeFilter;
    if (identityFilter) p.identity_id        = identityFilter;
    if (dateRange[0])   p.from_date          = dateRange[0].format("YYYY-MM-DD");
    if (dateRange[1])   p.to_date            = dateRange[1].format("YYYY-MM-DD");
    return { ...p, ...overrides };
  };

  const loadDocs = (overrides = {}) => dispatch(fetchKYCDocuments(buildParams(overrides)));

  useEffect(() => {
    loadDocs();
    dispatch(fetchInventoryCountries()); // used by the upload modal country dropdown
    dispatch(fetchIdentities());
  }, [dispatch]);

  // Fix 9: auto-apply — call immediately when a filter changes
  const applyFilters = (overrides = {}) => { setPage(1); loadDocs({ page: 1, ...overrides }); };
  const clearFilters = () => {
    setStatusFilter(undefined); setTypeFilter(undefined);
    setIdentityFilter(undefined); setDateRange([null, null]);
    setPage(1);
    dispatch(fetchKYCDocuments({ page: 1, per_page: 20 }));
  };
  const hasActiveFilter = !!(statusFilter || typeFilter || identityFilter || dateRange[0]);

  // Download a file via backend proxy
  const handleDownload = async (docId, role, docName) => {
    const key = `${docId}-${role}`;
    setDownloading(key);
    try {
      const res = await api.get(`/didlogic/kyc/documents/${docId}/files/${role}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${docName || "document"}-${role}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Download failed:", e);
    } finally {
      setDownloading(null);
    }
  };

  const columns = [
    {
      title: "Document",
      key: "doc",
      render: (_, r) => {
        const opt = DOC_TYPE_OPTIONS.find((o) => o.value === r.doc_type);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 42, height: 42, borderRadius: 10, flexShrink: 0,
              background: opt?.bg || "#f5f5f5",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <i className={`ti ${opt?.icon || "ti-file"}`} style={{ fontSize: 22, color: opt?.color || "#8c8c8c" }} />
            </div>
            <div>
              <div style={{ fontWeight: 600, color: "#1a1a2e", fontSize: 14 }}>
                {r.name || "—"}
              </div>
              <div style={{ fontSize: 11, color: "#8c8c8c" }}>
                ID #{r.id}
                {r.country && <span style={{ marginLeft: 8 }}>🌐 {r.country}</span>}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: "Type",
      dataIndex: "doc_type",
      key: "doc_type",
      render: (v) => <DocTypeTag docType={v} />,
    },
    {
      title: "Identity",
      key: "identity",
      render: (_, r) => r.identity ? (
        <div>
          <div style={{ fontWeight: 500, fontSize: 13 }}>
            {displayIdentityName(r.identity.name, suffix)}
          </div>
          <div style={{ fontSize: 11, color: "#8c8c8c" }}>ID #{r.identity_id}</div>
        </div>
      ) : <span style={{ color: "#bfbfbf" }}>—</span>,
    },
    {
      title: "Status",
      dataIndex: "approvement_status",
      key: "approvement_status",
      render: (v) => <StatusBadge status={v} />,
    },
    {
      title: "Uploaded",
      dataIndex: "created_at",
      key: "created_at",
      render: (v) => v ? dayjs(v).format("DD MMM YYYY HH:mm") : "—",
    },
    {
      title: "Files",
      dataIndex: "files",
      key: "files",
      render: (files, r) => {
        if (!files?.length) return <span style={{ color: "#bfbfbf", fontSize: 12 }}>—</span>;
        return (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {files.map((f) => (
              <Button
                key={f.role}
                size="small"
                style={{ borderRadius: 8, fontSize: 12 }}
                loading={downloading === `${r.id}-${f.role}`}
                onClick={() => handleDownload(r.id, f.role, r.name)}
                icon={<i className="ti ti-download" />}
              >
                {f.label || f.role}
              </Button>
            ))}
          </div>
        );
      },
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      render: (_, r) => (
        <div style={{ display: "flex", gap: 6 }}>
          <Tooltip title="Edit name & identity">
            <Button size="small" style={{ borderRadius: 8 }} onClick={() => setEditDoc(r)}
              icon={<i className="ti ti-edit" />} />
          </Tooltip>
          <Popconfirm
            title="Delete this document?"
            description="This is permanent and cannot be undone."
            onConfirm={() => dispatch(deleteKYCDocument(r.id))}
            okText="Delete" okButtonProps={{ danger: true }} cancelText="Cancel"
          >
            <Button size="small" danger style={{ borderRadius: 8 }} loading={deleting}
              icon={<i className="ti ti-trash" />} />
          </Popconfirm>
        </div>
      ),
    },
  ];

  // Stats
  const byStatus = (s) => documents.filter((d) => d.approvement_status?.toLowerCase() === s).length;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="card" style={{ padding: "20px 24px", borderRadius: 12 }}>

        {/* Header — no Upload button here (moved to filter row below) */}
        <div style={{ marginBottom: 24 }}>
          <h4 style={{ fontWeight: 700, marginBottom: 4 }}>
            <i className="ti ti-file-certificate me-2" style={{ color: "#667eea" }} />KYC Documents
          </h4>
          <p style={{ color: "#8c8c8c", marginBottom: 0, fontSize: 13 }}>
            Manage identity verification documents for telecom regulatory compliance
          </p>
        </div>

        {/* ── Section 1: Identities ── */}
        <div style={{
          background: "#f3f0ff",
          border: "1px solid #d8ceff",
          borderRadius: 14,
          padding: "18px 20px",
          marginBottom: 20,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <i className="ti ti-users" style={{ color: "#3C2371", fontSize: 16 }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: "#3C2371" }}>Identities</span>
            <span style={{ fontSize: 11, color: "#8e77fa", marginLeft: 4 }}>
              — group KYC documents by end-user
            </span>
          </div>
          <IdentitySection onCreateOpen={() => setCreateIdentityOpen(true)} />
        </div>

        {/* Info banner */}
        <div style={{
          background: "#f0f5ff", border: "1px solid #adc6ff",
          borderRadius: 12, padding: "14px 20px", marginBottom: 24,
          display: "flex", gap: 12, alignItems: "flex-start",
        }}>
          <i className="ti ti-info-circle" style={{ fontSize: 20, color: "#2f54eb", marginTop: 1, flexShrink: 0 }} />
          <div>
            <p style={{ fontWeight: 600, color: "#1d3557", marginBottom: 6, fontSize: 13 }}>
              When are KYC documents required?
            </p>
            <p style={{ color: "#4a6fa5", fontSize: 12, marginBottom: 8 }}>
              Certain countries require identity verification before phone numbers can be activated.
              Upload your documents and they will be reviewed for regulatory approval.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
              {DOC_TYPE_OPTIONS.map((o) => (
                <div key={o.value} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#4a6fa5" }}>
                  <i className={`ti ${o.icon}`} style={{ color: o.color }} />
                  <strong>{o.label}</strong>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Stats */}
        {documents.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 12, marginBottom: 24 }}>
            {[
              { label: "Total",     value: documents.length, color: "#667eea", bg: "#f0f0ff", icon: "ti-files" },
              { label: "Approved",  value: byStatus("approved"),  color: "#52c41a", bg: "#f6ffed", icon: "ti-circle-check" },
              { label: "Pending",   value: byStatus("pending"),   color: "#fa8c16", bg: "#fff7e6", icon: "ti-clock" },
              { label: "In Review", value: byStatus("in review"), color: "#722ed1", bg: "#f9f0ff", icon: "ti-eye" },
              { label: "Rejected",  value: byStatus("rejected"),  color: "#cf1322", bg: "#fff1f0", icon: "ti-circle-x" },
              { label: "Expired",   value: byStatus("expired"),   color: "#8c8c8c", bg: "#f5f5f5", icon: "ti-calendar-x" },
            ].map((s) => (
              <div key={s.label} style={{
                background: "#fff", border: "1px solid #f0f0f0",
                borderRadius: 12, padding: "12px 16px",
                display: "flex", alignItems: "center", gap: 10,
              }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: s.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className={`ti ${s.icon}`} style={{ fontSize: 18, color: s.color }} />
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "#1a1a2e" }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: "#8c8c8c" }}>{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Section 2: Documents — same brand color ── */}
        <div style={{
          background: "#f3f0ff",
          border: "1px solid #d8ceff",
          borderRadius: 14,
          padding: "18px 20px",
          marginBottom: 20,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <i className="ti ti-file-certificate" style={{ color: "#3C2371", fontSize: 16 }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: "#3C2371" }}>Documents</span>
            <span style={{ fontSize: 11, color: "#8e77fa", marginLeft: 4 }}>
              — upload and manage KYC documents
            </span>
          </div>

        {/* Filters + Upload button (Fix 3 & 9) */}
        <div className="kyc-filter-row" style={{
          background: "#fff", border: "1px solid #f0f0f0",
          borderRadius: 12, padding: "14px 20px", marginBottom: 20,
          display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end",
        }}>
          <div className="kyc-filter-item">
            <label style={{ display: "block", fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>Approval Status</label>
            <Select
              style={{ width: 160 }} placeholder="All statuses" allowClear value={statusFilter}
              onChange={(v) => { setStatusFilter(v); applyFilters({ approvement_status: v || undefined }); }}
            >
              <Option value="pending"><i className="ti ti-clock me-1" style={{ color: "#fa8c16" }} />Pending</Option>
              <Option value="in review"><i className="ti ti-eye me-1" style={{ color: "#722ed1" }} />In Review</Option>
              <Option value="approved"><i className="ti ti-circle-check me-1" style={{ color: "#52c41a" }} />Approved</Option>
              <Option value="rejected"><i className="ti ti-circle-x me-1" style={{ color: "#cf1322" }} />Rejected</Option>
              <Option value="expired"><i className="ti ti-calendar-x me-1" style={{ color: "#8c8c8c" }} />Expired</Option>
            </Select>
          </div>
          <div className="kyc-filter-item">
            <label style={{ display: "block", fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>Document Type</label>
            <Select
              style={{ width: 190 }} placeholder="All types" allowClear value={typeFilter}
              onChange={(v) => { setTypeFilter(v); applyFilters({ doc_type: v || undefined }); }}
            >
              {DOC_TYPE_OPTIONS.map((o) => (
                <Option key={o.value} value={o.value}>
                  <i className={`ti ${o.icon} me-1`} />{o.label}
                </Option>
              ))}
            </Select>
          </div>
          <div className="kyc-filter-item">
            <label style={{ display: "block", fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>Identity</label>
            <Select
              style={{ width: 180 }} placeholder="All identities" allowClear value={identityFilter} showSearch
              filterOption={(input, option) => option?.searchlabel?.toLowerCase().includes(input.toLowerCase())}
              onChange={(v) => { setIdentityFilter(v); applyFilters({ identity_id: v || undefined }); }}
            >
              {identities.map((id) => {
                const dn = displayIdentityName(id.name, suffix);
                return <Option key={id.id} value={id.id} searchlabel={dn}>{dn}</Option>;
              })}
            </Select>
          </div>
          <div className="kyc-filter-item">
            <label style={{ display: "block", fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>Upload Date</label>
            <RangePicker
              value={dateRange} format="DD MMM YYYY" style={{ borderRadius: 8 }}
              onChange={(v) => {
                setDateRange(v || [null, null]);
                applyFilters({
                  from_date: v?.[0]?.format("YYYY-MM-DD"),
                  to_date:   v?.[1]?.format("YYYY-MM-DD"),
                });
              }}
            />
          </div>

          {/* Clear — only visible when a filter is active */}
          {hasActiveFilter && (
            <Button style={{ borderRadius: 8 }} onClick={clearFilters}>
              <i className="ti ti-x me-1" />Clear
            </Button>
          )}

          {/* Upload Document button — now lives here (Fix 3) */}
          <Button
            type="primary"
            className="kyc-upload-btn"
            style={{ marginLeft: "auto", borderRadius: 8, background: "linear-gradient(135deg,#3C2371,#8e77fa)", border: "none" }}
            onClick={() => setUploadOpen(true)}
          >
            <i className="ti ti-upload me-2" />Upload Document
          </Button>
        </div>

        {/* Table */}
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #f0f0f0", overflow: "hidden" }}>
          <Table
            dataSource={documents}
            columns={columns}
            rowKey="id"
            loading={loading}
            scroll={{ x: "max-content" }}
            pagination={{
              pageSize: 20, current: page,
              onChange: (p) => { setPage(p); loadDocs({ page: p }); },
              style: { padding: "12px 16px" },
            }}
            locale={{
              emptyText: (
                <div style={{ padding: "60px 0", textAlign: "center" }}>
                  <i className="ti ti-file-off" style={{ fontSize: 48, color: "#bfbfbf", display: "block", marginBottom: 16 }} />
                  <p style={{ color: "#8c8c8c", marginBottom: 8, fontSize: 15 }}>No KYC documents found.</p>
                  <p style={{ color: "#bfbfbf", fontSize: 12, marginBottom: 16 }}>
                    Upload identity documents to comply with telecom regulations.
                  </p>
                  <Button
                    type="primary"
                    style={{ borderRadius: 8, background: "linear-gradient(135deg,#667eea,#764ba2)", border: "none" }}
                    onClick={() => setUploadOpen(true)}
                  >
                    <i className="ti ti-upload me-1" />Upload First Document
                  </Button>
                </div>
              ),
            }}
          />
        </div>

        </div>{/* end Documents section */}

        <CreateIdentityModal open={createIdentityOpen} onClose={() => setCreateIdentityOpen(false)} />
        <UploadModal
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          onDone={() => loadDocs({ page: 1 })}
          onCreateIdentity={() => { setUploadOpen(false); setCreateIdentityOpen(true); }}
        />
        <EditModal doc={editDoc} open={!!editDoc} onClose={() => setEditDoc(null)} onDone={() => loadDocs({ page })} />
        </div>{/* end card */}
      </div>
    </div>
  );
};

export default KYCDocuments;
